import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { TechnicalCaseStudy } from '../types';

const CaseStudies: React.FC = () => {
  const caseStudies: TechnicalCaseStudy[] = [
  {
    id: '1',
    title: 'Enterprise ERP Integration',
    client: 'Global Manufacturing Corp',
    industry: 'Manufacturing',
    challenge: 'Legacy ERP system with 15+ disconnected databases causing data inconsistencies and manual reconciliation consuming 200+ hours monthly.',
    solution: 'Custom API middleware connecting SAP ERP with Salesforce CRM, inventory management, and financial systems with real-time bidirectional sync.',
    results: [
    { id: '1', label: 'Time Saved', value: '180 hrs/mo', icon: 'Clock', trend: 'up' },
    { id: '2', label: 'Data Accuracy', value: '99.8%', icon: 'Target', trend: 'up' },
    { id: '3', label: 'Cost Reduction', value: '$85K/year', icon: 'DollarSign', trend: 'up' },
    { id: '4', label: 'Processing Speed', value: '12x faster', icon: 'Zap', trend: 'up' }],

    technologies: ['SAP API', 'Salesforce REST', 'PostgreSQL', 'Redis Cache', 'Docker'],
    image: "https://images.unsplash.com/photo-1719343881239-1d6cf7a44558",
    alt: 'Modern office workspace with multiple computer monitors displaying data dashboards and analytics charts'
  },
  {
    id: '2',
    title: 'Multi-Platform E-commerce Sync',
    client: 'Retail Innovations Inc',
    industry: 'E-commerce',
    challenge: 'Managing inventory across Shopify, Amazon, and custom marketplace with frequent overselling and manual updates causing customer dissatisfaction.',
    solution: 'Unified inventory management API with real-time stock synchronization, automated order routing, and intelligent pricing updates across all platforms.',
    results: [
    { id: '1', label: 'Overselling Reduced', value: '98%', icon: 'TrendingDown', trend: 'up' },
    { id: '2', label: 'Order Processing', value: '5x faster', icon: 'ShoppingCart', trend: 'up' },
    { id: '3', label: 'Revenue Increase', value: '34%', icon: 'TrendingUp', trend: 'up' },
    { id: '4', label: 'Customer Satisfaction', value: '4.8/5', icon: 'Star', trend: 'up' }],

    technologies: ['Shopify API', 'Amazon MWS', 'Node.js', 'MongoDB', 'RabbitMQ'],
    image: "https://images.unsplash.com/photo-1729774288643-1ae4839df482",
    alt: 'E-commerce warehouse with organized shelves of products and digital inventory management system'
  }];


  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Technical Case Studies
          </h2>
          <p className="text-lg text-text-secondary">
            Real-world integration challenges solved with measurable results
          </p>
        </div>
        
        <div className="space-y-12">
          {caseStudies.map((study) =>
          <div
            key={study.id}
            className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-fast">

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <Image
                  src={study.image}
                  alt={study.alt}
                  className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block px-3 py-1 bg-primary text-white text-sm font-medium rounded-full mb-2">
                      {study.industry}
                    </span>
                    <h3 className="text-2xl font-bold text-white">
                      {study.title}
                    </h3>
                  </div>
                </div>
                
                <div className="p-6 lg:p-8">
                  <div className="mb-6">
                    <div className="text-sm font-medium text-primary mb-2">Client</div>
                    <div className="text-lg font-semibold text-text-primary mb-4">
                      {study.client}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm font-medium text-text-primary mb-2">Challenge</div>
                        <p className="text-text-secondary text-sm leading-relaxed">
                          {study.challenge}
                        </p>
                      </div>
                      
                      <div>
                        <div className="text-sm font-medium text-text-primary mb-2">Solution</div>
                        <p className="text-text-secondary text-sm leading-relaxed">
                          {study.solution}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {study.results.map((result) =>
                  <div
                    key={result.id}
                    className="bg-surface p-4 rounded-lg border border-border">

                        <Icon name={result.icon} size={20} className="text-primary mb-2" />
                        <div className="text-2xl font-bold text-text-primary mb-1">
                          {result.value}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {result.label}
                        </div>
                      </div>
                  )}
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-text-primary mb-3">Technologies Used</div>
                    <div className="flex flex-wrap gap-2">
                      {study.technologies.map((tech, index) =>
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">

                          {tech}
                        </span>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default CaseStudies;